"use client";

import { useEffect, useState } from "react";
import { Analytics as VercelAnalytics } from "@vercel/analytics/next";
import { SpeedInsights as VercelSpeedInsights } from "@vercel/speed-insights/next";

/**
 * LazyAnalytics - Only loads Vercel Analytics after page becomes interactive
 *
 * This significantly improves TTI (Time to Interactive) by deferring
 * analytics script injection until after the main content is ready.
 *
 * Strategy:
 * 1. Wait for page to become fully interactive (requestIdleCallback)
 * 2. Additional 2 second delay to ensure all critical resources are loaded
 * 3. Then inject analytics scripts
 *
 * Expected TTI improvement: 300-800ms on mobile
 */
export function LazyAnalytics() {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any;

    // Function to trigger analytics loading
    const loadAnalytics = () => {
      // Add a small additional delay to ensure page is fully settled
      setTimeout(() => {
        setShouldLoad(true);
      }, 2000);
    };

    // Only run on client-side
    if (typeof window !== "undefined") {
      // Use requestIdleCallback if available (modern browsers)
      if ("requestIdleCallback" in win) {
        win.requestIdleCallback(loadAnalytics, { timeout: 5000 });
      } else {
        // Fallback: wait for load event + delay
        if (document.readyState === "complete") {
          loadAnalytics();
        } else {
          const handleLoad = () => {
            loadAnalytics();
          };
          win.addEventListener("load", handleLoad);
          return () => win.removeEventListener("load", handleLoad);
        }
      }
    }
  }, []);

  if (!shouldLoad) return null;

  return (
    <>
      <VercelAnalytics />
      <VercelSpeedInsights />
    </>
  );
}
